import { initializeApp } from "firebase/app";

import {
    GoogleAuthProvider,
    getAuth,
    sendPasswordResetEmail,
    signOut,
} from "firebase/auth";

import {
    getFirestore,
    query,
    getDocs,
    getDoc,
    collection,
    where,
    addDoc,
    setDoc,
    updateDoc,
    doc,
} from "firebase/firestore";
import {
    getStorage,
} from "firebase/storage"

import emailTemplates from "./emailTemplates";
import firebaseConfig from "./config/firebaseConfig";


const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const db = getFirestore(app);
const storage = getStorage(app);
const googleProvider = new GoogleAuthProvider();

// translate firebase error codes to human readable messages in german

const translateFirebaseError = (error) => {
    switch (error.code) {
        case "auth/invalid-email":
            return "Ungültige E-Mail-Adresse";
        case "auth/user-disabled":
            return "UUID ist deaktiviert";
        case "auth/user-not-found":
            return "UUID nicht gefunden";
        case "auth/wrong-password":
            return "Falsches Passwort";
        case "auth/email-already-in-use":
            return "E-Mail-Adresse ist bereits registriert";
        case "auth/weak-password":
            return "Passwort ist zu schwach - bitte mindestens 6 Zeichen";
        case "auth/operation-not-allowed":
            return "Operation nicht erlaubt";
        case "auth/too-many-requests":
            return "Zu viele Anfragen. Bitte versuchen Sie es später erneut";
        case "auth/requires-recent-login":
            return "Zu viele Anfragen. Bitte versuchen Sie es später erneut";

        default:
            return error.message;
    }
};

const setEmailTemplates = async () => {

    for (const category of emailTemplates) {
        const template = {
            subject: category.subject,
            html: category.html
        };

        const docRef = doc(db, "templates", category.name);
        const docSnap = await getDoc(docRef);
        await setDoc(docRef, template);
        if (!docSnap.exists()) {
            await setDoc(docRef, template);
        }
    }
};

const numberFormat = (value) =>
    new Intl.NumberFormat('de-DE', {
        style: 'currency',
        currency: 'EUR'
    }).format(value);

const formatPrice = (obj) => {
    // Check if obj is null, undefined, or not an object
    if (!obj || typeof obj !== "object" || Array.isArray(obj)) {
        return obj;
    }

    Object.keys(obj).forEach((key) => {
        if (obj[key] === undefined) {
            obj[key] = "nicht angegeben";
        }
        if (key === "price" || key === "Einzelpreis" || key === "nettoPrice" || key === "invoiceTotal" || key === "invoiceTaxes7" || key === "invoiceTaxes19" || key === "invoiceSubTotal") {
            obj[key] = obj[key] ? obj[key].toLocaleString('de-DE', { style: 'currency', currency: 'EUR' }) : obj[key];
        } else if (typeof obj[key] === "object" && obj[key] !== null) {
            formatPrice(obj[key]);
        }
    });
    return obj;
};

function parseEdiData(data) {
    // Deep clone so we don't mutate original
    const _data = JSON.parse(JSON.stringify(data));

    // EDI price formatting
    const formatEdiPrice = (val) => {
        if (typeof val === "number" && !isNaN(val)) return val.toFixed(2);
        if (typeof val === "string") {
            const num = parseFloat(val.replace(/[^0-9,.\-]/g, "").replace(",", "."));
            if (!isNaN(num)) return num.toFixed(2);
        }
        return val;
    };

    // EDI date formatting (supports dd.mm.yyyy, dd-mm-yyyy, etc.)
    const formatEdiDate = (val) => {
        if (!val) return "";
        const parts = val.split(/[.\-/]/);
        if (parts.length === 3) {
            const [d, m, y] = parts;
            const yyyy = y.length === 2 ? "20" + y : y;
            return `${yyyy.padStart(4, "0")}${m.padStart(2, "0")}${d.padStart(2, "0")}`;
        }
        const d = new Date(val);
        if (!isNaN(d.getTime())) {
            return d.toISOString().slice(0, 10).replace(/-/g, "");
        }
        return val;
    };

    // Format all cart items (object, not array)
    if (_data.cart && typeof _data.cart === "object") {
        Object.values(_data.cart).forEach(item => {
            if (item) {
                ["price", "nettoPrice", "tax"].forEach(key => {
                    if (item[key] !== undefined) item[key] = formatEdiPrice(item[key]);
                });
            }
        });
    }
    // Format relevant form fields
    if (_data.form && typeof _data.form === "object") {
        ["invoiceTotal", "invoiceTaxes7", "invoiceTaxes19", "invoiceSubTotal"].forEach(key => {
            if (_data.form[key] !== undefined) {
                _data.form[key] = formatEdiPrice(_data.form[key]);
            }
        });
        if (_data.form.orderDate) {
            _data.form.orderDate = formatEdiDate(_data.form.orderDate);
        }
    }
    _data.template = "orderEdi";
    return _data;
}

function generateEdiOrderText(json) {
    const pad = (val, len = 2) => String(val).padStart(len, '0');
    const now = new Date();
    const lookupId = `${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    // orderDate should be in format YYMMDD

    const orderDate = json.form.orderDate || now.toISOString().slice(0, 10).replace(/-/g, '');

    const orderDateShort = orderDate.slice(2, 8) // Falls json.form.orderDate bereits im Format YYYYMMDD ist

    // If orderDate is not provided, use current date in YYMMDD format
    const orderTime = json.form.orderTime
        ? json.form.orderTime.slice(0, 5).replace(':', '') // "09:30" → "0930"
        : `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}`;

    const buyer = `${json.form.accountID}0000000`.padEnd(14, '0').slice(0, 14);
    const buyerName = `${json.user.son} ${json.user.name}`;
    const buyerVAT = ""; // Tax ID or VAT number, if available
    const buyerAccountID = json.form.accountID || ""; // Assuming accountID is used as API reference

    const receiver = "1250320000000"; // This is a fixed value for the receiver
    // zur lang 35 Zeichen
    const receiverName = "Heine Fachmedien GmbH & Co. KG"; // Fixed receiver name
    const receiverId = "5heineb"; // Fixed receiver ID
    const receiverVAT = "42/628/02308";


    const email = json.user.email;
    // create unique identifier for the order
    const orderId = json.form.orderNumber || `${json.user.uid}-${lookupId}`;
    let message = json.form.message || "Bestellung über Litport";

    const cart = json.cart;
    let ediLines = [];

    // Header
    ediLines.push("UNA:+.? '");
    ediLines.push(`UNB+UNOC:3+${buyer}:14+${receiver}:14+${orderDateShort}:${orderTime}+${lookupId}'`);

    // Order
    ediLines.push(`UNH+${lookupId}+ORDERS:D:96A:UN:EAN008'`);
    ediLines.push(`BGM+220+${orderId}+9'`);
    ediLines.push(`DTM+137:${orderDate}:102'`);

    // Receiver
    ediLines.push(`NAD+SU+${receiver}::9++${receiverName}+++++DE'`);
    ediLines.push(`RFF+IA:${receiverId}'`);
    // ediLines.push(`RFF+VA:${receiverVAT}'`);

    // Buyer
    ediLines.push(`NAD+BY+${buyer}::9++${buyerName}+++++DE'`);
    ediLines.push(`RFF+API:${buyerAccountID}'`);
    // ediLines.push(`RFF+VA:${buyerVAT}'`);

    // Currency
    ediLines.push(`CUX+2:EUR:9'`); // 9 “Standard” or “default” rate (e.g., includes VAT)

    // Positionen
    let lineCount = 0;
    for (const [key, item] of Object.entries(cart)) {
        ediLines.push(`LIN+${lineCount + 1}++${item.isbn}:EN'`);
        ediLines.push(`PIA+5+${item.isbn}:IB'`); // Product ID
        ediLines.push(`IMD+L+010+:::${item.title}'`);
        ediLines.push(`QTY+21:${item.qty}'`);

        // Free text field for each item
        ediLines.push(`FTX+LIN+++${json.form.deliveryName} - ${message}'`); // Assuming deliveryName and message are available in form
        ediLines.push(`PRI+AAE:${item.price}::RTP'`);

        // Bestell-Nummer
        ediLines.push(`RFF+LI:${orderId}'`); // Assuming orderNumber is available in form

        // Kostenstelle
        ediLines.push(`RFF+BFN:${json.form.costCenter || ''}'`); // Assuming costCenter is available in form
        lineCount++;
    }


    // Bestell-Nummer
    ediLines.push(`RFF+LI:${orderId}'`); // Assuming orderNumber is available in form

    // Kostenstelle
    ediLines.push(`RFF+BFN:${json.form.costCenter || ''}'`); // Assuming costCenter is available in form


    // Summary
    ediLines.push(`UNS+S'`);
    ediLines.push(`CNT+2:${lineCount}'`);
    ediLines.push(`UNT+${ediLines.length - 1}+${lookupId}'`);
    ediLines.push(`UNZ+1+${lookupId}'`);

    return ediLines.join('\n');
}

const sendEmailTemplate = async (data) => {

    console.log("--Email--")
    let _data = JSON.parse(JSON.stringify(data));

    // Format price values before sending
    _data = formatPrice(_data);

    if (_data.user && _data.user.claims) {
        _data.user.edi = _data.user.claims.edi || false;
        _data.user.req = _data.user.claims.req || false;
    }

    // Get template configuration from database
    let templateConfig = null;
    try {
        templateConfig = await fetchEmailTemplate(data.template);
    } catch (error) {
        console.warn(`Template ${data.template} not found in database, using defaults`);
    }

    // Get global debug BCC configuration from localStorage
    let globalDebugBcc = null;
    try {
        const saved = localStorage.getItem('globalDebugBcc');
        if (saved) {
            globalDebugBcc = JSON.parse(saved);
        }
    } catch (error) {
        console.warn('Error reading global debug BCC config from localStorage:', error);
    }

    // Determine recipients based on template configuration
    const sendToUser = templateConfig?.sendToUser !== false; // Default to true if not set
    const sendToAdmin = templateConfig?.sendToAdmin !== false; // Default to true if not set

    console.log(`Template ${data.template} - sendToUser: ${sendToUser}, sendToAdmin: ${sendToAdmin}`);

    // Send to user if configured
    if (sendToUser && data.template !== "register") {
        // Check if custom recipient email is provided (for custom cart fields)
        const recipientEmail = _data.form?.recipientEmail || _data.user.email;
        const useCustomRecipient = _data.form?.recipientEmail && _data.form.recipientEmail !== _data.user.email;
        
        // Build email data
        const emailData = {
            to: recipientEmail,
            template: {
                name: _data.template,
                data: _data,
            },
        };
     
        // If using custom recipient, CC the original user
        if (useCustomRecipient) {
            emailData.cc = _data.user.email;
            console.log(`Using custom recipient: ${recipientEmail}, CC: ${_data.user.email}`);
        }
        
        // Add global debug BCC email if enabled
        if (globalDebugBcc?.enabled && globalDebugBcc?.email) {
            emailData.bcc = globalDebugBcc.email;
            console.log(`Adding global debug BCC: ${globalDebugBcc.email}`);
        }

        await addDoc(collection(db, "mail"), emailData);
        console.log(`✅ Email sent to user: ${recipientEmail}`);
    }

    // Send to admin if configured
    if (sendToAdmin) {
        let adminEmail = firebaseConfig.email; // Default admin email
        
        // Check for custom admin email override
        if (templateConfig?.adminEmailOverride) {
            adminEmail = templateConfig.adminEmailOverride;
            console.log(`Using custom admin email: ${adminEmail}`);
        } 

        const isEdi = _data.user?.edi === true && ["orderCart"].includes(_data.template);
        console.log("isEdi:", isEdi);

        if (isEdi) {
            adminEmail = firebaseConfig.emailEdi;
            _data = parseEdiData(_data);
            const ediMessage = generateEdiOrderText(_data);
            _data.ediMessage = ediMessage;
            console.log("EDI Test Output:\n", ediMessage);
        } 

        // Build admin email data
        const adminEmailData = {
            to: adminEmail,
            template: {
                name: _data.template,
                data: _data,
            },
        };
        
        // Add global debug BCC email if enabled
        if (globalDebugBcc?.enabled && globalDebugBcc?.email) {
            adminEmailData.bcc = globalDebugBcc.email;
            console.log(`Adding global debug BCC to admin email: ${globalDebugBcc.email}`);
        }

        await addDoc(collection(db, "mail"), adminEmailData);
        console.log(`✅ Email sent to admin: ${adminEmail}`);
    }

    console.log("Email sending completed");
};

// Function to fetch all email templates from Firestore
const fetchEmailTemplates = async () => {
    try {
        const templatesCollection = collection(db, 'templates');
        const templatesSnapshot = await getDocs(templatesCollection);
        
        const templates = [];
        templatesSnapshot.forEach((doc) => {
            templates.push({
                id: doc.id,
                ...doc.data()
            });
        });
        
        return templates;
    } catch (error) {
        console.error('Error fetching email templates:', error);
        throw error;
    }
};

// Function to fetch a specific template by ID
const fetchEmailTemplate = async (templateId) => {
    try {
        const templateDoc = doc(db, 'templates', templateId);
        const templateSnap = await getDoc(templateDoc);
        
        if (templateSnap.exists()) {
            return {
                id: templateSnap.id,
                ...templateSnap.data()
            };
        } else {
            throw new Error('Template not found');
        }
    } catch (error) {
        console.error('Error fetching email template:', error);
        throw error;
    }
};

// Function to update an email template
const updateEmailTemplate = async (templateId, templateData) => {
    try {
        console.log('Attempting to update template:', templateId, 'with data:', templateData);
        const templateDoc = doc(db, 'templates', templateId);
        
        // Check if user is authenticated
        const user = auth.currentUser;
        if (!user) {
            throw new Error('User not authenticated');
        }
        console.log('User authenticated:', user.uid);
        
        // Try updateDoc first, fallback to setDoc if needed
        try {
            await updateDoc(templateDoc, templateData);
            console.log('Template updated successfully with updateDoc:', templateId);
        } catch (updateError) {
            console.log('updateDoc failed, trying setDoc:', updateError.message);
            // Fallback to setDoc (this will overwrite the entire document)
            await setDoc(templateDoc, templateData, { merge: true });
            console.log('Template updated successfully with setDoc:', templateId);
        }
    } catch (error) {
        console.error('Error updating email template:', error);
        console.error('Error details:', {
            code: error.code,
            message: error.message,
            templateId,
            templateData
        });
        throw error;
    }
};

const sendPasswordReset = async (email) => {
    try {
        await sendPasswordResetEmail(auth, email);
        alert("Password reset link sent!");
    } catch (err) {
        console.error(err);
        alert(err.message);
    }
};

const logout = () => {
    signOut(auth);
};

export {
    auth,
    db,
    sendPasswordReset,
    logout,

    sendEmailTemplate,
    translateFirebaseError,
    firebaseConfig,
    fetchEmailTemplates,
    fetchEmailTemplate,
    updateEmailTemplate,
};

// Action code settings for password reset
export const getPasswordResetActionSettings = () => {
    const baseUrl = window.location.hostname === 'localhost'
        ? (firebaseConfig.siteUrl || window.location.origin)
        : window.location.origin;
    const actionUrl = `${baseUrl}/password-reset-success`;
    console.log('Password reset action URL:', actionUrl);
    
    return {
        url: actionUrl,
        handleCodeInApp: false,
    };
};

// Action code settings for email verification
export const getEmailVerificationActionSettings = () => {
    const baseUrl = window.location.hostname === 'localhost'
        ? (firebaseConfig.siteUrl || window.location.origin)
        : window.location.origin;
    const actionUrl = `${baseUrl}/email-verify-success`;
    console.log('Email verification action URL:', actionUrl);

    return {
        url: actionUrl,
        handleCodeInApp: false,
    };
};
